import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { markSessionStarted } from "../utils/authSession";
import { loadMyPageAccess } from "../utils/pageAccess";
import { isInstallerNativeApp } from "../utils/installerSms";
import {
    hydrateInstallerSession,
    persistInstallerSession,
    resolvePortalPath
} from "../utils/installerSession";
import '../assets/css/main.css';
import '../styles/InstallerLogin.css';
import '../styles/LandingLogin.css';

const Login = () => {
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [nativeApp] = useState(() =>
        isInstallerNativeApp()
    );
    const [restoring, setRestoring] = useState(() =>
        isInstallerNativeApp()
    );
    const navigate = useNavigate();

    useEffect(() => {
        const restoreSession = async () => {
            try {
                await hydrateInstallerSession();

                const token =
                    localStorage.getItem('token');
                const userRaw =
                    localStorage.getItem('user');

                if (!token || !userRaw) {
                    return;
                }

                let user;
                try {
                    user = JSON.parse(userRaw);
                } catch {
                    return;
                }

                try {
                    const response = await axios.post(
                        '/api/auth/refresh',
                        {},
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                    if (response.data?.token) {
                        localStorage.setItem(
                            'token',
                            response.data.token
                        );
                    }
                    if (response.data?.user) {
                        localStorage.setItem(
                            'user',
                            JSON.stringify(
                                response.data.user
                            )
                        );
                        user = response.data.user;
                    }
                } catch (refreshError) {
                    /*
                     * Если токен ещё жив, но refresh
                     * недоступен — всё равно пускаем.
                     * 401 — сессия мертва.
                     */
                    if (
                        refreshError.response
                            ?.status === 401
                    ) {
                        localStorage.removeItem(
                            'token'
                        );
                        localStorage.removeItem(
                            'user'
                        );
                        return;
                    }
                }

                markSessionStarted();
                await persistInstallerSession();

                try {
                    await loadMyPageAccess();
                } catch (_) {
                    /* ignore */
                }

                navigate(
                    resolvePortalPath(user.role_id),
                    { replace: true }
                );
            } finally {
                setRestoring(false);
            }
        };

        restoreSession();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const response = await axios.post(
                "/api/auth/login",
                { login, password },
                {
                    withCredentials: true,
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (response.data.message === "Авторизация успешна") {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                markSessionStarted();
                await persistInstallerSession();

                try {
                    await loadMyPageAccess();
                } catch (_) {
                    /* fallback на роли */
                }

                navigate(
                    resolvePortalPath(
                        response.data.user?.role_id
                    ),
                    { replace: true }
                );
            }
        } catch (error) {
            if (error.response) {
                setError(error.response.data.message || "Неверный логин или пароль");
            } else {
                setError("Ошибка соединения с сервером");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const loginForm = compact => (
        <form
            className={compact ? 'login100-form' : 'sap-landing-form'}
            onSubmit={handleSubmit}
        >
            {compact ? (
                <span className="login100-form-title">
                    CRM Монтажник
                </span>
            ) : null}

            {error ? (
                <p className={compact ? 'errorMessage' : 'sap-landing-error'}>
                    {error}
                </p>
            ) : null}

            {compact ? (
                <>
                    <div className="wrap-input100 validate-input m-b-23">
                        <span className="label-input100">Пользователь</span>
                        <input
                            className="input100"
                            type="text"
                            placeholder="Введите логин"
                            value={login}
                            onChange={e => setLogin(e.target.value)}
                        />
                    </div>
                    <div
                        className="wrap-input100 validate-input m-b-23 password-wrapper"
                        style={{ position: 'relative' }}
                    >
                        <span className="label-input100">Пароль</span>
                        <input
                            className="input100"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Введите пароль"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                        <span
                            className="toggle-password"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                position: 'absolute',
                                right: '15px',
                                top: '38px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                color: '#666'
                            }}
                        >
                            {showPassword ? '👁️' : '🙈'}
                        </span>
                    </div>
                    <div className="container-login100-form-btn">
                        <div className="wrap-login100-form-btn">
                            <div className="login100-form-bgbtn"></div>
                            <button
                                className="login100-form-btn"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? 'Вход…' : 'Войти'}
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="sap-landing-field">
                        <label htmlFor="sap-login">Логин</label>
                        <input
                            id="sap-login"
                            type="text"
                            autoComplete="username"
                            placeholder="Введите логин"
                            value={login}
                            onChange={e => setLogin(e.target.value)}
                        />
                    </div>
                    <div className="sap-landing-field sap-landing-pass">
                        <label htmlFor="sap-password">Пароль</label>
                        <input
                            id="sap-password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder="Введите пароль"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label="Показать пароль"
                        >
                            {showPassword ? '🙈' : '👁️'}
                        </button>
                    </div>
                    <button
                        className="sap-landing-submit"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Вход…' : 'Войти в систему'}
                    </button>
                    <p className="sap-landing-note">
                        Корпоративный доступ сотрудников Смартком
                    </p>
                </>
            )}
        </form>
    );

    if (restoring && nativeApp) {
        return (
            <div className="limiter installer-login-page">
                <div className="container-login100">
                    <div
                        className="wrap-login100 p-l-55 p-r-55 p-t-65 p-b-54"
                        style={{ textAlign: 'center' }}
                    >
                        <span className="login100-form-title">
                            CRM Монтажник
                        </span>
                        <p style={{ color: '#4b5563' }}>
                            Восстановление сессии…
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (nativeApp) {
        return (
            <div className="limiter installer-login-page">
                <div className="container-login100">
                    <div className="wrap-login100 p-l-55 p-r-55 p-t-65 p-b-54">
                        {loginForm(true)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sap-landing">
            <div className="sap-landing-inner">
                <header className="sap-landing-top">
                    <div className="sap-landing-brand">
                        <img
                            className="sap-landing-logo"
                            src="/brand/smartcom-logo.jpg"
                            alt="Смарткомсервис"
                        />
                        <div>
                            <h1>СМАРТКОМСЕРВИС</h1>
                            <span>SAP · sap.smartcoms.ru</span>
                        </div>
                    </div>
                    <div className="sap-landing-host">sap.smartcoms.ru</div>
                </header>

                <div className="sap-landing-grid">
                    <section className="sap-landing-copy">
                        <span className="sap-landing-kicker">
                            Корпоративная система
                        </span>
                        <h2>
                            SAP Смартком — система управления процессами
                        </h2>
                        <p className="sap-landing-lead">
                            SAP Смартком (sap.smartcoms.ru) — корпоративная
                            система ООО «СМАРТКОМСЕРВИС» для управления
                            проектами, задачами, объектами, кадрами и
                            календарём. Портал сопровождает полный цикл
                            строительства и эксплуатации базовых станций.
                        </p>

                        <div className="sap-landing-stats">
                            <div className="sap-landing-stat">
                                <b>10 лет</b>
                                <span>на рынке связи</span>
                            </div>
                            <div className="sap-landing-stat">
                                <b>4 000+</b>
                                <span>базовых станций</span>
                            </div>
                            <div className="sap-landing-stat">
                                <b>6</b>
                                <span>регионов эксплуатации</span>
                            </div>
                            <div className="sap-landing-stat">
                                <b>100%</b>
                                <span>выполненных обязательств</span>
                            </div>
                        </div>

                        <div className="sap-landing-partners">
                            <span>Теле2</span>
                            <span>МТС</span>
                            <span>Мегафон</span>
                            <span>Ростелеком</span>
                            <span>Билайн</span>
                        </div>

                        <ul className="sap-landing-points">
                            <li>
                                Надёжный партнёр крупнейших операторов
                                сотовой связи Российской Федерации
                            </li>
                            <li>
                                Полный комплекс работ по строительству
                                и модернизации объектов сотовой связи:
                                проектирование, экспертизы, логистика,
                                строительно-монтажные работы
                            </li>
                            <li>
                                Сплочённая команда высококвалифицированных
                                специалистов
                            </li>
                            <li>
                                Эксплуатация объектов сотовой связи
                                в Москве и в 5 регионах Поволжья
                            </li>
                            <li>
                                География работ: Российская Федерация
                            </li>
                        </ul>

                        <dl className="sap-landing-legal">
                            <div>
                                <dt>Компания</dt>
                                <dd>ООО «СМАРТКОМСЕРВИС»</dd>
                            </div>
                            <div>
                                <dt>Дата регистрации</dt>
                                <dd>21 сентября 2016 года</dd>
                            </div>
                            <div>
                                <dt>ОГРН</dt>
                                <dd>1166313135120</dd>
                            </div>
                            <div>
                                <dt>ИНН / КПП</dt>
                                <dd>6318018716 / 631801001</dd>
                            </div>
                            <div>
                                <dt>ОКПО</dt>
                                <dd>04746430</dd>
                            </div>
                            <div>
                                <dt>Вид деятельности</dt>
                                <dd>43.21 Производство электромонтажных работ (+ ещё 35 видов)</dd>
                            </div>
                            <div className="full">
                                <dt>Юридический адрес</dt>
                                <dd>
                                    443090, Самарская область, г. Самара,
                                    ул. Ставропольская, д. 3, этаж 9, позиция 8
                                </dd>
                            </div>
                            <div className="full">
                                <dt>Организационно-правовая форма</dt>
                                <dd>Общество с ограниченной ответственностью</dd>
                            </div>
                        </dl>
                    </section>

                    <aside className="sap-landing-auth">
                        <div className="sap-landing-card">
                            <img
                                className="sap-landing-card-logo"
                                src="/brand/smartcom-logo.jpg"
                                alt="Смарткомсервис"
                            />
                            <h3>Вход в SAP</h3>
                            <p className="hint">
                                Авторизация сотрудников Смартком
                            </p>
                            {loginForm(false)}
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default Login;
